import { useCallback, useState } from 'react';

import { window } from '../../../globals';
import type { ReportLogoAction } from '../../../components/projects/generic/ReportGenerationV2/shared/types';

import useRemoveOrganizationLogo from './useRemoveOrganizationLogo';
import useUploadOrganizationLogo from './useUploadOrganizationLogo';

const defaultImageUrl = '/img/fossa-primary-logo.svg';
const organizationImageUrl = '/organization/logo';

/**
 * Shared hook for managing report logo state and save logic.
 * Used by both project and release group report settings pages.
 */
export function useReportLogoSettings() {
  const [reportLogoAction, setReportLogoAction] = useState<ReportLogoAction | null>(null);
  const [imageUrl, setImageUrl] = useState<string>(
    window.user?.organization?.image ? organizationImageUrl : defaultImageUrl,
  );
  const [isImageDisplayFailed, setIsImageDisplayFailed] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const { mutateAsync: uploadLogo, isPending: isUploading } = useUploadOrganizationLogo();
  const { mutateAsync: removeLogo, isPending: isRemoving } = useRemoveOrganizationLogo();

  const setFile = useCallback((file: File) => {
    if (imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    const fileUrl = URL.createObjectURL(file);
    setIsImageDisplayFailed(false);
    setImageUrl(fileUrl);
    setImageFile(file);
    setReportLogoAction('SAVE');
  }, [imageUrl]);

  const unsetFile = useCallback(() => {
    setIsImageDisplayFailed(false);
    setImageUrl(defaultImageUrl);
    setImageFile(null);
    setReportLogoAction('REMOVE');
  }, []);

  const handleImgError = useCallback(() => {
    setIsImageDisplayFailed(true);
  }, []);

  const isLoading = isUploading || isRemoving;
  const isDirty = reportLogoAction !== null;
  const isSaveDisabled = !isDirty || isImageDisplayFailed || isLoading || !window.user?.organizationId;

  const onSave = useCallback(async () => {
    const organizationId = window.user?.organizationId;
    if (!organizationId) {
      return;
    }

    try {
      if (reportLogoAction === 'SAVE' && imageFile) {
        await uploadLogo({ organizationId, file: imageFile });
      }
      if (reportLogoAction === 'REMOVE') {
        await removeLogo();
      }
      setReportLogoAction(null);
    } catch {
      // Error handling is done by useToastedMutation in the individual hooks
    }
  }, [reportLogoAction, imageFile, uploadLogo, removeLogo]);

  return {
    fileUrl: imageUrl,
    setFile,
    unsetFile,
    handleImgError,
    onSave,
    isDirty,
    isSaveDisabled,
    isLoading,
  };
}
